// Client-side secure password generation & entropy estimation

const SETS = {
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  numbers: "0123456789",
  symbols: "!@#$%^&*()-_=+[]{};:,.<>?/",
};

// Ambiguous / easily confused characters
const AMBIGUOUS = "Il1O0o|`'\";:.,{}[]()";

export function buildCharset(options) {
  let chars = "";
  if (options.uppercase) chars += SETS.uppercase;
  if (options.lowercase) chars += SETS.lowercase;
  if (options.numbers) chars += SETS.numbers;
  if (options.symbols) chars += SETS.symbols;

  if (options.excludeAmbiguous) {
    chars = chars
      .split("")
      .filter((c) => !AMBIGUOUS.includes(c))
      .join("");
  }
  // Dedupe
  return Array.from(new Set(chars.split(""))).join("");
}

// Tirage entier CSPRNG non biaisé dans [0, maxExclusive) par rejection sampling.
// N'utilise QUE window.crypto.getRandomValues — jamais Math.random ni % charset.length.
export function secureRandomInt(maxExclusive) {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
    throw new Error("secureRandomInt: maxExclusive doit être un entier positif");
  }
  if (maxExclusive === 1) return 0;
  // Plus grand multiple de maxExclusive <= 2^32, borne de rejet pour éliminer le biais modulo.
  const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
  const buf = new Uint32Array(1);
  let x;
  do {
    window.crypto.getRandomValues(buf);
    x = buf[0];
  } while (x >= limit);
  return x % maxExclusive;
}

export function generatePassword(length, options) {
  const charset = buildCharset(options);
  if (!charset) return "";
  let out = "";
  for (let i = 0; i < length; i++) {
    out += charset[secureRandomInt(charset.length)];
  }
  return out;
}

export function entropyBits(length, options) {
  const charset = buildCharset(options);
  if (!charset) return 0;
  return Math.round(length * Math.log2(charset.length));
}

// Entropy estimated from the actual password characters (works for custom input)
export function entropyBitsForString(pw) {
  if (!pw) return 0;
  let pool = 0;
  if (/[a-z]/.test(pw)) pool += 26;
  if (/[A-Z]/.test(pw)) pool += 26;
  if (/[0-9]/.test(pw)) pool += 10;
  if (/[^a-zA-Z0-9]/.test(pw)) pool += 32;
  if (pool === 0) return 0;
  return Math.round(pw.length * Math.log2(pool));
}

// Estimation basée sur l'entropie du mécanisme. Returns { level 0-4, label, color }
export const STRENGTH_LEVELS = 5;
export function strength(bits) {
  if (bits < 40) return { level: 0, label: "Faible", color: "#EF4444" };
  if (bits < 50) return { level: 1, label: "Moyen", color: "#F59E0B" };
  if (bits < 60) return { level: 2, label: "Robuste", color: "#06B6D4" };
  if (bits < 80) return { level: 3, label: "Sûr", color: "#10B981" };
  return { level: 4, label: "Très sûr", color: "#059669" };
}
